import * as React from 'react';
import * as XLSX from 'xlsx';
import styled from 'styled-components'

import { Dialog } from '@longline/aqua-ui/services/Dialog';
import { Dropzone } from '@longline/aqua-ui/controls/Dropzone';
import { PrimaryButton } from '@longline/aqua-ui/controls/PrimaryButton';
import { TabBar } from '@longline/aqua-ui/controls/TabBar';

import { TCellValue } from '../../util/TCellValue';
import { ExcelTable } from './ExcelTable';
import { sheetToArray, TSheetToArrayResult } from '../../util/sheetToArray';
import { OptionsForm } from './OptionsForm';
import { Sprites } from '../../svg';
import { SVG } from '@longline/aqua-ui/svg';
import { ILayer } from '../../types/ILayer';
import { LayerUtils } from '../../util/LayerUtils';
import { ELayerType } from '../../types/ELayerType';
import { ExcelColumn } from './IExcelColumn';

// A module that allows 
// * uploading of an Excel file
// * Display sheets and (partial) content
// * Allows mapping of data sources to columns
// * Performs import of data sources into a DataSet.

type TPhase = 'upload' | 'configure' | 'error';

interface IProps {
  /** ignore */
  className?: string;
  /** Fired when import is cancelled. */
  onCancel: () => void;
  /** Fired when import is complete. */
  onImport: (layers: ILayer[]) => void;

  columns?: ExcelColumn[];
  /**
   * Dialog title
   * @default "Import data from Excel"
   */
  title?: string;
}

const ExcelImport = (props: IProps) => {
  const [phase, setPhase] = React.useState<TPhase>('upload'); // Operating mode
  const [error, setError] = React.useState(""); // Current error
  const [workbook, setWorkbook] = React.useState<XLSX.WorkBook | null>(null); // Current workbook in memory
  const [data, setData] = React.useState<TCellValue[][]>([]); // Data array read from workbook
  const [sheetIndex, setSheetIndex] = React.useState(0); // Current worksheet index
  const [totalRows, setTotalRows] = React.useState(0);
  const [skiprows, setSkiprows] = React.useState("0"); // Number of header rows to skip
  const [reduction, setReduction] = React.useState("1"); // Data reduction: skip every n rows
  const [valid, setValid] = React.useState(true); // Is options form valid?
  const [loading, setLoading] = React.useState(false); // Currently loading?
  const [filename, setFilename] = React.useState<string | null>(null); // Name of imported file.

  // Column index for each importable variable. null means "not assigned".
  const [latitude, setLatitude] = React.useState<number | null>(null);
  const [longitude, setLongitude] = React.useState<number | null>(null);
  const [depth, setDepth] = React.useState<number | null>(null);
  const [eastSpeed, setEastSpeed] = React.useState<number | null>(null);
  const [northSpeed, setNorthSpeed] = React.useState<number | null>(null);
  const [yearlyPOC, setYearlyPOC] = React.useState<number | null>(null);
  const [dailyPOC, setDailyPOC] = React.useState<number | null>(null);
  const [oxygen, setOxygen] = React.useState<number | null>(null);
  const [ammonia, setAmmonia] = React.useState<number | null>(null);
  const [chlorophyll, setChlorophyll] = React.useState<number | null>(null);
  const [pathogen, setPathogen] = React.useState<number | null>(null);
  const [pocInSediment, setPocInSediment] = React.useState<number | null>(null);
  const [pomInSediment, setPomInSediment] = React.useState<number | null>(null);
  const [bacteriaInSediment, setBacteriaInSediment] = React.useState<number | null>(null);
  const [oxygenInSediment, setOxygenInSediment] = React.useState<number | null>(null);
  const [sulphateInSediment, setSulphateInSediment] = React.useState<number | null>(null);
  const [ironHydroxideInSediment, setIronHydroxideInSediment] = React.useState<number | null>(null);
  const [totalSulphideInSediment, setTotalSulphideInSediment] = React.useState<number | null>(null);
  const [ironInSediment, setIronInSediment] = React.useState<number | null>(null);
  const [ironSulphideInSediment, setIronSulphideInSediment] = React.useState<number | null>(null);
  const [therapeutants, setTherapeutants] = React.useState<number | null>(null);
  
  const handleCancelImport = () => {
    props.onCancel();
  }

  //
  // Upon user file selection:
  // * Read file from disk as ArrayBuffer
  // * Load Excel Workbook from ArrayBuffer
  // On error, state is set to 'error'.
  // See: https://github.com/exceljs/exceljs/issues/832
  // 
  const handleAddFiles = (files: File[]) => {
    const file = files[0];
    const reader = new FileReader();
    setFilename(file.name);
    reader.readAsArrayBuffer(file);
    reader.onerror = () => {
      setPhase('error');
      setError('The file could not be read.');
    }
    reader.onload = () => {
      const buffer = reader.result;
      try {
        const workbook = XLSX.read(buffer);
        if(workbook.SheetNames[0] == 'FINS Metadata') {
          loadOrganix(file.name, workbook);
        } else {
          loadData(workbook, 0);
        }
      }
      catch(e) {
        setPhase('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }

  const loadOrganix = (filename: string, workbook: XLSX.WorkBook) => {
    // Read entire worksheet into array.
    const result = sheetToArray(workbook, 1);
    
    // Remove rows to be skipped.
    result.data.splice(0, result.skiprows);

    const layers: ILayer[] = [];
    if(result.dailyPOC != null) layers.push(createLayer(filename, result, ELayerType.DailyPOC, result.dailyPOC));
    if(result.yearlyPOC != null) layers.push(createLayer(filename, result, ELayerType.YearlyPOC, result.yearlyPOC));
    if(result.oxygen != null) layers.push(createLayer(filename, result, ELayerType.Oxygen, result.oxygen));
    if(result.ammonia != null) layers.push(createLayer(filename, result, ELayerType.Ammonia, result.ammonia));
    if(result.chlorophyll != null) layers.push(createLayer(filename, result, ELayerType.Chlorophyll, result.chlorophyll));
    if(result.pathogen != null) layers.push(createLayer(filename, result, ELayerType.Pathogen, result.pathogen));

    if(result.pocInSediment != null) layers.push(createLayer(filename, result, ELayerType.PocInSediment, result.pocInSediment));
    if(result.pomInSediment != null) layers.push(createLayer(filename, result, ELayerType.PomInSediment, result.pomInSediment));
    if(result.bacteriaInSediment != null) layers.push(createLayer(filename, result, ELayerType.BacteriaInSediment, result.bacteriaInSediment));
    if(result.oxygenInSediment != null) layers.push(createLayer(filename, result, ELayerType.OxygenInSediment, result.oxygenInSediment));
    if(result.sulphateInSediment != null) layers.push(createLayer(filename, result, ELayerType.SulphateInSediment, result.sulphateInSediment));
    if(result.ironHydroxideInSediment != null) layers.push(createLayer(filename, result, ELayerType.IronHydroxideInSediment, result.ironHydroxideInSediment));
    if(result.totalSulphideInSediment != null) layers.push(createLayer(filename, result, ELayerType.TotalSulphideInSediment, result.totalSulphideInSediment));
    if(result.ironInSediment != null) layers.push(createLayer(filename, result, ELayerType.IronInSediment, result.ironInSediment));
    if(result.ironSulphideInSediment != null) layers.push(createLayer(filename, result, ELayerType.IronSulphideInsediment, result.ironSulphideInSediment));
    if(result.therapeutants != null) layers.push(createLayer(filename, result, ELayerType.Therapeutants, result.therapeutants));
    
    props.onImport(layers);      
  }

  const loadData = (workbook: XLSX.WorkBook, sheetIndex: number) => {
    // First, switch to configure mode and show loader.
    setPhase('configure');
    setSheetIndex(sheetIndex);
    setWorkbook(workbook);
    setLoading(true);

    // Next, load the data from the worksheet and hide loader:
    setTimeout(() => {
      const result = sheetToArray(workbook, sheetIndex, 20);
      setData([...result.data]);
      setTotalRows(result.totalRows);
      setSkiprows(result.skiprows.toString());
      setReduction("1");
      if(!props.columns || props.columns.includes('latitude')) setLatitude(result.latitude);
      if(!props.columns || props.columns.includes('longitude')) setLongitude(result.longitude);
      if(!props.columns || props.columns.includes('depth')) setDepth(result.depth);
      if(!props.columns || props.columns.includes('u')) setEastSpeed(result.eastspeed);
      if(!props.columns || props.columns.includes('v')) setNorthSpeed(result.northspeed);
      if(!props.columns || props.columns.includes('daily_poc')) setDailyPOC(result.dailyPOC);
      if(!props.columns || props.columns.includes('yearly_poc')) setYearlyPOC(result.yearlyPOC);
      if(!props.columns || props.columns.includes('oxygen')) setOxygen(result.oxygen);
      if(!props.columns || props.columns.includes('ammonia')) setAmmonia(result.ammonia);
      if(!props.columns || props.columns.includes('chlorophyll')) setChlorophyll(result.chlorophyll);
      if(!props.columns || props.columns.includes('pathogen')) setPathogen(result.pathogen);
      if(!props.columns || props.columns.includes('poc_in_sediment')) setPocInSediment(result.pocInSediment);
      if(!props.columns || props.columns.includes('pom_in_sediment')) setPomInSediment(result.pomInSediment);
      if(!props.columns || props.columns.includes('bacteria_sediment')) setBacteriaInSediment(result.bacteriaInSediment);
      if(!props.columns || props.columns.includes('oxygen_in_sediment')) setOxygenInSediment(result.oxygenInSediment);
      if(!props.columns || props.columns.includes('sulphate_in_sediment')) setSulphateInSediment(result.sulphateInSediment);
      if(!props.columns || props.columns.includes('iron_hydroxide_in_sediment')) setIronHydroxideInSediment(result.ironHydroxideInSediment);
      if(!props.columns || props.columns.includes('total_sulphide_in_sediment')) setTotalSulphideInSediment(result.totalSulphideInSediment);
      if(!props.columns || props.columns.includes('iron_in_sediment')) setIronInSediment(result.ironInSediment);
      if(!props.columns || props.columns.includes('iron_sulphide_in_sediment')) setIronSulphideInSediment(result.ironSulphideInSediment);
      if(!props.columns || props.columns.includes('therapeutants')) setTherapeutants(result.therapeutants);
      setLoading(false);
    }, 100);
  }

  const handleChangeSheet = (idx: number) => {
    if(!workbook) return;
    loadData(workbook, idx);
  }

  const handleChangeOptions = (skiprows: string, reduction: string, valid: boolean) => {
    setSkiprows(skiprows);
    setReduction(reduction);
    setValid(valid);
  }

  const handleSetColumn = (idx: number, value: ExcelColumn) => {
    if(latitude == idx) setLatitude(null);
    if(longitude == idx) setLongitude(null);
    if(depth == idx) setDepth(null);
    if(eastSpeed == idx) setEastSpeed(null);
    if(northSpeed == idx) setNorthSpeed(null);
    if(yearlyPOC == idx) setYearlyPOC(null);
    if(dailyPOC == idx) setDailyPOC(null);
    if(oxygen == idx) setOxygen(null);
    if(ammonia == idx) setAmmonia(null);
    if(chlorophyll == idx) setChlorophyll(null);
    if(pathogen == idx) setPathogen(null);
    if(pocInSediment == idx) setPocInSediment(null);
    if(pomInSediment == idx) setPomInSediment(null);
    if(bacteriaInSediment == idx) setBacteriaInSediment(null);
    if(oxygenInSediment == idx) setOxygenInSediment(null);
    if(sulphateInSediment == idx) setSulphateInSediment(null);
    if(ironHydroxideInSediment == idx) setIronHydroxideInSediment(null);
    if(totalSulphideInSediment == idx) setTotalSulphideInSediment(null);
    if(ironInSediment == idx) setIronInSediment(null);
    if(ironSulphideInSediment == idx) setIronSulphideInSediment(null);
    if(therapeutants == idx) setTherapeutants(null);

    switch(value) {
      case 'latitude': setLatitude(idx); break;
      case 'longitude': setLongitude(idx); break;
      case 'depth': setDepth(idx); break;
      case 'u': setEastSpeed(idx); break;
      case 'v': setNorthSpeed(idx); break;
      case 'daily_poc': setDailyPOC(idx); break;
      case 'yearly_poc': setYearlyPOC(idx); break;
      case 'oxygen': setOxygen(idx); break;
      case 'ammonia': setAmmonia(idx); break;
      case 'chlorophyll': setChlorophyll(idx); break;
      case 'pathogen': setPathogen(idx); break;
      case 'poc_in_sediment': setPocInSediment(idx); break;
      case 'pom_in_sediment': setPomInSediment(idx); break;
      case 'bacteria_sediment': setBacteriaInSediment(idx); break;
      case 'oxygen_in_sediment': setOxygenInSediment(idx); break;
      case 'sulphate_in_sediment': setSulphateInSediment(idx); break;
      case 'iron_hydroxide_in_sediment': setIronHydroxideInSediment(idx); break;
      case 'total_sulphide_in_sediment': setTotalSulphideInSediment(idx); break;
      case 'iron_in_sediment': setIronInSediment(idx); break;
      case 'iron_sulphide_in_sediment': setIronSulphideInSediment(idx); break;
      case 'therapeutants': setTherapeutants(idx); break;
    }
  }

  const isColumnAssignmentValid = () => {
    // Lat/lng are required.
    if(latitude == null) return false;
    if(longitude == null) return false;
    // If importing east speed, must also import north speed (and vice versa)
    if(eastSpeed == null && northSpeed != null) return false;
    if(northSpeed == null && eastSpeed != null) return false;
    // Must import at least one variable.
    if(depth == null && eastSpeed == null && dailyPOC == null && 
        yearlyPOC == null && oxygen == null && ammonia == null && chlorophyll == null &&
        pathogen == null 
        && pocInSediment == null
        && pomInSediment == null
        && bacteriaInSediment == null
        && oxygenInSediment == null
        && sulphateInSediment == null
        && ironHydroxideInSediment == null
        && totalSulphideInSediment == null
        && ironInSediment == null
        && ironSulphideInSediment == null
        && therapeutants == null) return false;
    return true;
  }

  const createLayer = (filename: string, result: TSheetToArrayResult, type: ELayerType, ...indices: number[]): ILayer => {
    const lngCol = result.longitude!;
    const latCol = result.latitude!;
    const layer = LayerUtils.createLayer(type, filename,
      result.data.map((row: TCellValue[]) => {
        const obj = {
          lng: parseFloat(row[lngCol] as string),
          lat: parseFloat(row[latCol] as string),
          value: indices.map(idx => parseFloat(row[idx] as string))
        };
        return obj;
     }));
    return layer;
  }

  const handleImport = () => {
    // Guard: import button is only enabled once lat/lng columns are picked
    // and a workbook is loaded — but TS can't narrow that from state alone.
    if(!workbook || !filename || latitude === null || longitude === null) return;

    setLoading(true);

    // Read entire worksheet into array.
    const result = sheetToArray(workbook, sheetIndex);

    // Remove rows to be skipped.
    result.data.splice(0, parseInt(skiprows));

    // Copy user-select lat/lng columns into result; it's used by createLayer.
    result.latitude = latitude;
    result.longitude = longitude;

    const layers: ILayer[] = [];

    if(depth != null) layers.push(createLayer(filename, result, ELayerType.Depth, depth));
    if(dailyPOC != null) layers.push(createLayer(filename, result, ELayerType.DailyPOC, dailyPOC));
    if(yearlyPOC != null) layers.push(createLayer(filename, result, ELayerType.YearlyPOC, yearlyPOC));
    if(oxygen != null) layers.push(createLayer(filename, result, ELayerType.Oxygen, oxygen));
    if(ammonia != null) layers.push(createLayer(filename, result, ELayerType.Ammonia, ammonia));
    if(chlorophyll != null) layers.push(createLayer(filename, result, ELayerType.Chlorophyll, chlorophyll));
    if(pathogen != null) layers.push(createLayer(filename, result, ELayerType.Pathogen, pathogen));
    if(pocInSediment != null) layers.push(createLayer(filename, result, ELayerType.PocInSediment, pocInSediment));
    if(pomInSediment != null) layers.push(createLayer(filename, result, ELayerType.PomInSediment, pomInSediment));
    if(bacteriaInSediment != null) layers.push(createLayer(filename, result, ELayerType.BacteriaInSediment, bacteriaInSediment));
    if(oxygenInSediment != null) layers.push(createLayer(filename, result, ELayerType.OxygenInSediment, oxygenInSediment));
    if(sulphateInSediment != null) layers.push(createLayer(filename, result, ELayerType.SulphateInSediment, sulphateInSediment));
    if(ironHydroxideInSediment != null) layers.push(createLayer(filename, result, ELayerType.IronHydroxideInSediment, ironHydroxideInSediment));
    if(totalSulphideInSediment != null) layers.push(createLayer(filename, result, ELayerType.TotalSulphideInSediment, totalSulphideInSediment));
    if(ironInSediment != null) layers.push(createLayer(filename, result, ELayerType.IronInSediment, ironInSediment));
    if(ironSulphideInSediment != null) layers.push(createLayer(filename, result, ELayerType.IronSulphideInsediment, ironSulphideInSediment));
    if(therapeutants != null) layers.push(createLayer(filename, result, ELayerType.Therapeutants, therapeutants));
    if(eastSpeed != null && northSpeed != null) layers.push(createLayer(filename, result, ELayerType.CurrentSpeed, eastSpeed, northSpeed));

    props.onImport(layers);
  }

  const renderUpload = () => {
    return (
      <Dialog inverted width={800} open={true} onClose={handleCancelImport}>
        <Dialog.Header>
          {props.title || "Import data from Microsoft Excel"}
        </Dialog.Header>
        <Dialog.Content>
          <div> {/* <div> required to avoid Chrome horizontal scrollbar */}
            <Dropzone onAddFiles={handleAddFiles} accept=".xls,.xlsx"/>
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <PrimaryButton icon={SVG.Icons.Cross} onClick={handleCancelImport}>Close</PrimaryButton>
        </Dialog.Footer>
      </Dialog>      
    );
  }

  const renderConfigure = () => {
    return (
      <Dialog inverted width={1000} open={true} onClose={handleCancelImport}>
        <Dialog.Header>
          {props.title || "Import data from Microsoft Excel"}
        </Dialog.Header>
        <Dialog.Content nopadding>
          <ContentFlex>
            <TabBar line onChange={(idx: number) => handleChangeSheet(idx)}>
              {workbook?.SheetNames.map(s => <TabBar.Tab key={s}>{s}</TabBar.Tab>)}
            </TabBar>

            <ExcelTable 
              columns={props.columns}
              data={data} 
              rowCount={totalRows}
              skiprows={parseInt(skiprows)}
              loading={loading}
              longitude={longitude}
              latitude={latitude}
              depth={depth}
              eastspeed={eastSpeed}
              northspeed={northSpeed}
              daily_poc={dailyPOC}
              yearly_poc={yearlyPOC}
              oxygen={oxygen}
              ammonia={ammonia}
              chlorophyll={chlorophyll}
              pathogen={pathogen}
              pocInSediment={pocInSediment}
              pomInSediment={pomInSediment}
              bacteriaInSediment={bacteriaInSediment}
              oxygenInSediment={oxygenInSediment}
              sulphateInSediment={sulphateInSediment}
              ironHydroxideInSediment={ironHydroxideInSediment}
              totalSulphideInSediment={totalSulphideInSediment}
              ironInSediment={ironInSediment}
              ironSulphideInSediment={ironSulphideInSediment}
              therapeutants={therapeutants}
              onSetColumn={handleSetColumn}
            />

            <OptionsForm 
              skiprows={skiprows} 
              reduction={reduction} 
              totalrows={totalRows}
              onChange={handleChangeOptions}
            />
          </ContentFlex>
        </Dialog.Content>
        <Dialog.Footer align='space-between'>
          <PrimaryButton icon={SVG.Icons.Cross} onClick={handleCancelImport}>Close</PrimaryButton>
          <PrimaryButton icon={Sprites.Icons.Import} disabled={!valid || data.length == 0 || !isColumnAssignmentValid()} onClick={handleImport}>Import</PrimaryButton>
        </Dialog.Footer>        
      </Dialog>
    )
  }

  const renderError = () => {
    return (
      <Dialog width={800} open={true} onClose={handleCancelImport}>
        <Dialog.Header>
          Data import error
        </Dialog.Header>
        <Dialog.Content>
          An error occurred importing the data. The error was:
          {error}
        </Dialog.Content>
        <Dialog.Footer>
          <PrimaryButton onClick={handleCancelImport}>CANCEL</PrimaryButton>
        </Dialog.Footer>
      </Dialog>     
    );
  }

  switch(phase) {
    case 'configure': return renderConfigure();
    case 'error': return renderError();
    default: return renderUpload();
  }
}

const ContentFlex = styled.div`
  height: 500px;
  position: relative;
  display: flex;
  flex-direction: column;
`

export { ExcelImport }
